import { miscConstants } from '~/consts.ts';
import logger from '~/utils/logger';

export enum LastUsedLoginMethodType {
  SOCIAL = 'social',
  SSO = 'sso',
  OTP = 'otp',
  PASSKEYS = 'passkeys'
}

type LastUsedLoginMethodForEnvHost = {
  t: string; // type, e.g., "email"
  i?: string; // identifier of the auth method, e.g., 12345678
};

type LastUsedLoginLocalStorageData = {
  // e.g., "auth.scalekit.com"
  [d: string]: LastUsedLoginMethodForEnvHost;
};

const getDataFromLocalStorage = () => {
  try {
    return JSON.parse(
      localStorage.getItem(
        miscConstants.localStorageKeys.getLastUsedLoginMethodKey()
      ) || '{}'
    ) as LastUsedLoginLocalStorageData;
  } catch (e) {
    logger.error('Error parsing last used login method from localStorage', e);
    return {};
  }
};

const lastUsedLoginMethodData = (() => {
  const data = getDataFromLocalStorage();
  return {
    get: function () {
      return data;
    },
    add: function (type: LastUsedLoginMethodType, id?: string) {
      data[window.location.host] = {
        t: type,
        ...(id && { i: id })
      };
    },
    save: function () {
      localStorage.setItem(
        miscConstants.localStorageKeys.getLastUsedLoginMethodKey(),
        JSON.stringify(data)
      );
    }
  };
})();

const getLastUsedLoginMethodByEnvHost =
  (): LastUsedLoginMethodForEnvHost | null => {
    const data = lastUsedLoginMethodData.get();
    return data[window.location.host] || null;
  };

const isEmailLastUsedLoginMethod = (): boolean => {
  const lastUsedLoginMethod = getLastUsedLoginMethodByEnvHost();
  return (
    lastUsedLoginMethod?.t === LastUsedLoginMethodType.OTP ||
    lastUsedLoginMethod?.t === LastUsedLoginMethodType.SSO
  );
};

const isSocialLastUsedLoginMethod = (authMethodId: string): boolean => {
  const lastUsedLoginMethod = getLastUsedLoginMethodByEnvHost();
  return (
    lastUsedLoginMethod?.t === LastUsedLoginMethodType.SOCIAL &&
    lastUsedLoginMethod?.i === authMethodId
  );
};

const isPasskeysLastUsedLoginMethod = (authMethodId: string): boolean => {
  const lastUsedLoginMethod = getLastUsedLoginMethodByEnvHost();
  return (
    lastUsedLoginMethod?.t === LastUsedLoginMethodType.PASSKEYS &&
    lastUsedLoginMethod?.i === authMethodId
  );
};

const updateLastUsedLoginMethod = (
  type: LastUsedLoginMethodType,
  id?: string
): void => {
  lastUsedLoginMethodData.add(type, id);
  lastUsedLoginMethodData.save();
};

export {
  isEmailLastUsedLoginMethod,
  isSocialLastUsedLoginMethod,
  isPasskeysLastUsedLoginMethod,
  updateLastUsedLoginMethod
};
